import { Injectable, inject, signal, computed, effect } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { ApiError } from '../../abstract/api-error';
import { BaseService } from '../../abstract/base.service';
import { Field } from '../data-models/field.model';
import { FieldRequest } from '../request-models/field-request.model';
import { FieldFlightPath } from '../response-models/field-flight-path.model';
import { FieldOption } from '../response-models/field-option.model';
import { toFieldRequest } from '../utils/field-request-utils';
import { isRecord } from '../utils/is-record';
import { countOrZero, idString, nullableBoolean, nullableNumber, nullableString } from '../utils/wire-value-utils';
import { OrganizationContextService } from '../services/organization-context.service';

/**
 * Fields of the current organization: `/fields[/:id]`, `/fields/options`, `/fields/:id/flight-paths` and
 * `/fields/:id/sync-agremo` (hub `user-fields-controller.ts`). The hub scopes every route to the organization
 * the auth interceptor sends.
 *
 * Holds the loaded fields and the dropdown options as signal state. CRUD failures set `error` (the hub's
 * message) and resolve to `null` / `false`; `syncToAgremo` rejects with an `ApiError`.
 */
@Injectable({
  providedIn: 'root'
})
export class FieldService extends BaseService<Field> {
  private organizationContext = inject(OrganizationContextService);

  // Signal-based state management for fields
  private fieldsSignal = signal<Field[]>([]);
  private fieldOptionsSignal = signal<FieldOption[]>([]);
  private loadingSignal = signal<boolean>(false);
  private errorSignal = signal<string | null>(null);

  // Public computed signals for components
  readonly fields = computed(() => this.fieldsSignal());
  readonly fieldOptions = computed(() => this.fieldOptionsSignal());
  readonly isLoading = computed(() => this.loadingSignal());
  readonly error = computed(() => this.errorSignal());

  constructor() {
    super('fields', Field);
    // React to organization context changes — load fields when org becomes available
    effect(() => {
      const orgId = this.organizationContext.currentOrganizationId();
      if (orgId) {
        this.loadFields();
        this.loadFieldOptions();
      }
    });
  }

  /**
   * Load one page of fields (the hub caps `limit` at 100). Keeps the current list on failure.
   */
  async loadFields(
    customerId?: string,
    cropType?: string,
    search?: string,
    page: number = 1,
    limit: number = 100
  ): Promise<void> {
    if (!this.organizationContext.getCurrentOrganizationId()) {
      return;
    }

    const query = { customerId: customerId || undefined, cropType: cropType || undefined, search: search || undefined, page, limit };
    const result = await this.track('Failed to load fields', () => firstValueFrom(this.getPage({ query })));
    if (result) {
      this.fieldsSignal.set(result.items);
    }
  }

  /**
   * Load field options from API (for dropdowns)
   */
  private async loadFieldOptions(): Promise<void> {
    if (!this.organizationContext.getCurrentOrganizationId()) {
      return;
    }

    try {
      const options = await firstValueFrom(this.request('GET', { path: 'options' }, toFieldOptions));
      this.fieldOptionsSignal.set(options);
    } catch (error: unknown) {
      console.error('[FieldService] Error loading field options:', ApiError.fromHttpError(error).message);
    }
  }

  /**
   * Field options (synchronous, from the cached signal) as `Field`s, for dropdowns.
   */
  getFieldOptions(): Field[] {
    return this.fieldOptionsSignal().map(opt => new Field({
      id: opt.id,
      customerId: opt.customerId,
      name: opt.name,
      acreage: opt.acreage,
      cropType: opt.cropType,
      latitude: opt.latitude,
      longitude: opt.longitude,
      hasBoundary: opt.hasBoundary,
      agremoFieldId: opt.agremoFieldId
    }));
  }

  /**
   * Refresh field options from API
   */
  async refreshFieldOptions(): Promise<void> {
    await this.loadFieldOptions();
  }

  /**
   * Get all fields (from signal)
   */
  getFields(): Field[] {
    return this.fieldsSignal();
  }

  /**
   * Get field by ID (from signal)
   */
  getFieldById(id: string): Field | undefined {
    return this.fieldsSignal().find(f => f.id === id);
  }

  /**
   * Get fields by customer ID (from signal)
   */
  getFieldsByCustomerId(customerId: string): Field[] {
    return this.fieldsSignal().filter(f => f.customerId === customerId);
  }

  /**
   * Get field by ID from API (includes the boundary, which the list omits)
   */
  async fetchFieldById(id: string): Promise<Field | null> {
    return this.track('Failed to fetch field', () => firstValueFrom(this.get(id)));
  }

  /**
   * Logged drone flights on this field that have a flight path, for importing a boundary. Rejects with an `ApiError`.
   */
  async getFlightPaths(fieldId: string): Promise<FieldFlightPath[]> {
    return firstValueFrom(this.request('GET', { id: fieldId, path: 'flight-paths' }, toFlightPaths));
  }

  /**
   * Create a new field
   */
  async createField(dto: FieldRequest): Promise<Field | null> {
    const created = await this.track('Failed to create field', () => firstValueFrom(this.create(toFieldRequest(dto))));
    if (created) {
      this.fieldsSignal.update(fields => [...fields, created]);
      this.fieldOptionsSignal.update(options => [...options, toFieldOption(created)]);
    }
    return created;
  }

  /**
   * Update an existing field. Send `null` for an optional field the user emptied.
   */
  async updateField(id: string, dto: FieldRequest): Promise<Field | null> {
    const saved = await this.track('Failed to update field', () => firstValueFrom(this.update(id, toFieldRequest(dto))));
    if (saved) {
      this.replaceCached(saved);
    }
    return saved;
  }

  /**
   * Delete (soft-delete) a field
   */
  async deleteField(id: string): Promise<boolean> {
    const deleted = await this.track('Failed to delete field', async () => {
      await firstValueFrom(this.delete(id));
      return true;
    });
    if (deleted) {
      this.fieldsSignal.update(fields => fields.filter(f => f.id !== id));
      this.fieldOptionsSignal.update(options => options.filter(o => o.id !== id));
    }
    return deleted ?? false;
  }

  /**
   * Sync a field to Agremo: creates (or updates) the field in Agremo and stores the returned agremoFieldId.
   * Requires the field to have a boundary. Rejects with an `ApiError`.
   */
  async syncToAgremo(fieldId: string): Promise<Field> {
    const synced = await firstValueFrom(this.request('POST', { id: fieldId, path: 'sync-agremo' }, data => this.createType(data), {}));
    this.replaceCached(synced);
    return synced;
  }

  /**
   * Clear error state
   */
  clearError(): void {
    this.errorSignal.set(null);
  }

  /** Swaps a saved field into the cached list. The list's `hasBoundary` is kept when the response lacks it. */
  private replaceCached(saved: Field): void {
    this.fieldsSignal.update(fields => fields.map(f => {
      if (f.id !== saved.id) {
        return f;
      }
      saved.hasBoundary ??= saved.boundaryGeojson === undefined ? f.hasBoundary : saved.boundaryGeojson !== null;
      return saved;
    }));
  }

  /**
   * Runs an action with the loading flag set. A failure records the hub's message (or `fallback`) in `error`
   * and resolves to `null`.
   */
  private async track<R>(fallback: string, action: () => Promise<R>): Promise<R | null> {
    this.loadingSignal.set(true);
    this.errorSignal.set(null);
    try {
      return await action();
    } catch (error: unknown) {
      const message = ApiError.fromHttpError(error).errors[0] ?? fallback;
      console.error(`[FieldService] ${fallback}:`, message);
      this.errorSignal.set(message);
      return null;
    } finally {
      this.loadingSignal.set(false);
    }
  }
}

function toFieldOptions(data: unknown): FieldOption[] {
  return Array.isArray(data) ? data.map(toFieldOption) : [];
}

/** Reads one options row; also builds a row from a created `Field`. */
function toFieldOption(data: unknown): FieldOption {
  const source = isRecord(data) ? data : {};
  return {
    id: idString(source['id']),
    name: nullableString(source['name']) ?? '',
    acreage: countOrZero(source['acreage']),
    cropType: nullableString(source['cropType']) ?? '',
    customerId: nullableString(source['customerId']) ?? null,
    customerName: nullableString(source['customerName']) ?? null,
    latitude: nullableNumber(source['latitude']) ?? null,
    longitude: nullableNumber(source['longitude']) ?? null,
    hasBoundary: nullableBoolean(source['hasBoundary']) ?? !!source['boundaryGeojson'],
    agremoFieldId: nullableString(source['agremoFieldId']) ?? null,
  };
}

function toFlightPaths(data: unknown): FieldFlightPath[] {
  return Array.isArray(data) ? data.map(toFlightPath) : [];
}

function toFlightPath(data: unknown): FieldFlightPath {
  const source = isRecord(data) ? data : {};
  // The hub returns the stored flight path here: a bare coordinate list, or the form's FlightPathData object.
  const path = source['coordinates'];
  const nested = isRecord(path) ? path['coordinates'] : path;
  const points = Array.isArray(nested) ? nested : [];
  return {
    id: idString(source['id']),
    name: nullableString(source['name']) ?? '',
    date: nullableString(source['date']) ?? '',
    coordinates: points.filter(isLatLng).map(p => ({ lat: p.lat, lng: p.lng })),
  };
}

function isLatLng(value: unknown): value is { lat: number; lng: number } {
  return isRecord(value) && typeof value['lat'] === 'number' && typeof value['lng'] === 'number';
}
